/**
 * SizilPro — /settings
 * Client settings: account info, billing portal link, danger zone.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ExternalLink, CreditCard, Palette, Loader2, User } from "lucide-react";
import { useAuth } from "@/shared/hooks/useAuth";
import { useCurrentClient } from "@/shared/hooks/useCurrentClient";
import { openBillingPortal } from "@/lib/stripe";
import { useNotifications } from "@/shared/hooks/useNotifications";
import { PageHeader } from "@/shared/components/PageHeader";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const { name, email, role } = useAuth();
  const { client }            = useCurrentClient();
  const notify                = useNotifications();
  const [billingLoading, setBillingLoading] = useState(false);

  const handleBillingPortal = async () => {
    setBillingLoading(true);
    try {
      await openBillingPortal();
    } catch {
      notify.error("Could not open billing portal", "Try again or contact support.");
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <PageHeader title="Settings" />

        {/* ── Account ── */}
        <Section title="Account">
          <Row label="Name"         value={name  ?? "—"} />
          <Row label="Email"        value={email ?? "—"} />
          <Row label="Role"         value={role  ?? "—"} />
          <Row label="Company"      value={client?.name ?? "—"} />
          <Row label="Industry"     value={client?.industry ?? "—"} />
        </Section>

        {/* ── Proposal Builder ── */}
        <Section title="Proposal Builder">
          <div className="flex items-center justify-between py-3.5 border-b border-white/[0.06] last:border-0">
            <div>
              <p className="text-sm font-medium">Brand setup</p>
              <p className="text-xs text-white/40 mt-0.5">
                Logo, colors, cover letter, service menu
              </p>
            </div>
            <Link
              to="/proposal-builder/setup"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
            >
              <Palette className="w-3.5 h-3.5" />
              Edit setup
            </Link>
          </div>
        </Section>

        {/* ── Billing ── */}
        <Section title="Billing">
          <div className="flex items-center justify-between py-3.5">
            <div>
              <p className="text-sm font-medium">Subscription & invoices</p>
              <p className="text-xs text-white/40 mt-0.5">
                Manage your plan, payment method, and billing history
              </p>
            </div>
            <button
              type="button"
              onClick={handleBillingPortal}
              disabled={billingLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              {billingLoading
                ? <Loader2    className="w-3.5 h-3.5 animate-spin" />
                : <CreditCard className="w-3.5 h-3.5" />}
              Billing portal
              <ExternalLink className="w-3 h-3 opacity-40" />
            </button>
          </div>
        </Section>

        {/* ── Support ── */}
        <Section title="Support">
          <div className="flex items-center justify-between py-3.5">
            <div>
              <p className="text-sm font-medium">Get help</p>
              <p className="text-xs text-white/40 mt-0.5">
                Contact the SizilPro team
              </p>
            </div>
            <a
              href="mailto:support@sizilpro.com"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
            >
              <User className="w-3.5 h-3.5" />
              Email support
            </a>
          </div>
        </Section>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-8">
      <p className="text-[11px] uppercase tracking-[0.2em] text-white/30 font-medium mb-3">
        {title}
      </p>
      <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/[0.06]">
        {children}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5">
      <span className="text-sm text-white/40">{label}</span>
      <span className="text-sm text-white/70 font-medium">{value}</span>
    </div>
  );
}
